import React, { useContext } from 'react';

import { controlFocusRing } from '../controlFocusRing';
import { RadioGroupItemContext } from './RadioGroup';

interface RadioButtonProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (event: unknown) => void;
  label?: string;
  ariaLabel?: string;
  size?: 'default' | 'small';
  variant?: 'default' | 'primary' | 'accent';
  // Opt-in, as DataDisplayTable's: most radios sit on surfaces that stay dark in light mode.
  supportsLightTheme?: boolean;
  'data-testid'?: string;
}

export const RadioButton: React.FC<RadioButtonProps> = ({
  checked,
  disabled,
  onChange,
  label,
  ariaLabel,
  size = 'default',
  variant = 'default',
  supportsLightTheme = false,
  'data-testid': dataTestId,
}) => {
  const groupItem = useContext(RadioGroupItemContext);
  const light = (classes: string) => (supportsLightTheme ? classes : '');
  const sizeClasses = size === 'small' ? 'h-4 w-4' : 'h-6 w-6';

  const outerCircleClasses = `rounded-full border-2 ${
    disabled
      ? 'border-gray-800 bg-gray-800'
      : variant === 'default'
        ? checked
          ? `border-white ${light('light:border-gray-900')}`
          : `border-white ${light('light:border-gray-900')}`
        : variant === 'primary'
          ? checked
            ? 'border-pink-400'
            : 'border-pink-400'
          : variant === 'accent'
            ? checked
              ? 'border-pink-500'
              : 'border-pink-500'
            : ''
  }`;

  const innerCircleClasses = `rounded-full ${
    disabled
      ? 'bg-gray-800'
      : variant === 'default'
        ? `bg-white ${light('light:bg-gray-900')}`
        : variant === 'primary'
          ? 'bg-pink-400'
          : variant === 'accent'
            ? 'bg-pink-500'
            : ''
  }`;
  const labelClasses = `${
    size === 'small' ? 'text-sm' : 'text-base'
  } font-heebo ml-2 ${disabled ? 'text-gray-800' : `text-white ${light('light:text-gray-900')}`}`;

  const name = ariaLabel ?? (label || undefined);
  // Inside a RadioGroup only its tab stop is tabbable, and the arrow keys move between radios. An
  // unnamed radio stays out of the tab order: its caller owns the selection (selectionColumn).
  const tabIndex = !name ? undefined : disabled || (groupItem && !groupItem.isTabStop) ? -1 : 0;

  // Space only selects: unlike a click, it never calls onChange on a checked radio, whose handler
  // may toggle (TanStack's getToggleSelectedHandler).
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== ' ' || disabled) return;
    event.preventDefault();
    if (!checked && !event.repeat) onChange(event);
  };

  return (
    <div
      className={`flex cursor-pointer items-center ${controlFocusRing(supportsLightTheme)}`}
      data-testid={dataTestId}
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-label={name}
      onClick={!disabled ? onChange : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
    >
      <div
        className={`flex items-center justify-center ${sizeClasses} ${outerCircleClasses} transition-all duration-300`}
      >
        {checked && (
          <div
            className={`${innerCircleClasses} ${size === 'small' ? 'h-2 w-2' : 'h-3 w-3'}`}
          ></div>
        )}
      </div>
      {label && <span className={labelClasses}>{label}</span>}
    </div>
  );
};
